/**
 * First-class map catalog for the multi-building campus.
 * Layout geometry lives in personal-office-layout / campus builders;
 * this module is labels, theme, and join defaults only.
 */

export type MapTheme = "outdoor" | "indoor";

export type MapMeta = {
  id: string;
  label: string;
  theme: MapTheme;
  /** Zone id when the player is not inside a named common zone / pod. */
  defaultZoneId: string;
  /** Whether this map may host personal office pods. */
  allowsOffices: boolean;
};

/** Outdoor hub — join spawn. */
export const DEFAULT_JOIN_MAP_ID = "campus";

/**
 * All built-in maps (seed + editor tabs + defaults).
 * Order: outdoor first, then buildings as arranged on campus.
 */
export const MAP_REGISTRY: readonly MapMeta[] = [
  {
    id: "campus",
    label: "Central Campus",
    theme: "outdoor",
    defaultZoneId: "campus_lawn",
    allowsOffices: false,
  },
  {
    id: "reception",
    label: "Reception",
    theme: "indoor",
    defaultZoneId: "lobby",
    allowsOffices: false,
  },
  {
    id: "cafe",
    label: "Cafe",
    theme: "indoor",
    defaultZoneId: "cafe_floor",
    allowsOffices: false,
  },
  {
    id: "delivery",
    label: "Delivery",
    theme: "indoor",
    defaultZoneId: "commons",
    allowsOffices: true,
  },
  {
    id: "post_delivery",
    label: "Post Delivery",
    theme: "indoor",
    defaultZoneId: "commons",
    allowsOffices: true,
  },
  {
    id: "people_marketing",
    label: "People + Marketing",
    theme: "indoor",
    defaultZoneId: "commons",
    allowsOffices: true,
  },
  {
    id: "customer_success",
    label: "Customer Success",
    theme: "indoor",
    defaultZoneId: "commons",
    allowsOffices: true,
  },
] as const;

export const MAP_IDS = MAP_REGISTRY.map((m) => m.id);
export type MapId = (typeof MAP_IDS)[number];

const BY_ID = new Map(MAP_REGISTRY.map((m) => [m.id, m]));

export function getMapMeta(mapId: string | undefined | null): MapMeta {
  const id = (mapId ?? DEFAULT_JOIN_MAP_ID).trim() || DEFAULT_JOIN_MAP_ID;
  const found = BY_ID.get(id);
  if (found) return found;
  const campus = BY_ID.get(DEFAULT_JOIN_MAP_ID);
  if (campus) return campus;
  // Registry is never empty in code; last-resort outdoor stub.
  return {
    id: DEFAULT_JOIN_MAP_ID,
    label: "Central Campus",
    theme: "outdoor",
    defaultZoneId: "campus_lawn",
    allowsOffices: false,
  };
}

export function isKnownMapId(mapId: string): boolean {
  return BY_ID.has(mapId);
}

export function mapLabel(mapId: string | undefined | null): string {
  return getMapMeta(mapId).label;
}

export function isOutdoorMap(mapId: string | undefined | null): boolean {
  return getMapMeta(mapId).theme === "outdoor";
}

/** Legacy ids remapped once for mid-cutover clients / stale DB rows. */
export function resolveMapIdAlias(mapId: string | undefined | null): string {
  const raw = (mapId ?? DEFAULT_JOIN_MAP_ID).trim() || DEFAULT_JOIN_MAP_ID;
  if (raw === "plaza") return "campus";
  if (raw === "hq") return "reception";
  return raw;
}
